/**
 * admin/src/pages/SubscriptionsPage.tsx
 * Quản lý subscriptions — cấp thủ công và huỷ
 */

import { useState } from 'react';
import { useSubscriptions, useSubsMutations } from '../hooks/useSubscriptions';
import {
  DataTable,
  Badge,
  TierBadge,
  Pagination,
  SearchInput,
  Modal,
  ErrorBanner,
  Toggle,
  formatDate,
  formatDateTime,
} from '../components/ui/index';
import type { AdminSubscription } from '../types/admin';
import type { Column } from '../components/ui/index';

type SubRow = AdminSubscription & Record<string, unknown>;

// ─── Grant modal form state ───────────────────────────────────────────────────
interface GrantForm {
  userId: string;
  plan: 'basic' | 'premium' | 'family';
  days: number;
  note: string;
}

const DEFAULT_GRANT: GrantForm = {
  userId: '',
  plan: 'basic',
  days: 30,
  note: '',
};

export function SubscriptionsPage() {
  // ─── Filters & pagination ────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState<'basic' | 'premium' | 'family' | ''>('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [page, setPage] = useState(1);

  // ─── Modal state ─────────────────────────────────────────────────────────
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedSub, setSelectedSub] = useState<AdminSubscription | null>(null);
  const [grantForm, setGrantForm] = useState<GrantForm>(DEFAULT_GRANT);
  const [formError, setFormError] = useState('');

  // ─── Data ────────────────────────────────────────────────────────────────
  const {
    subs: subsData,
    total,
    totalPages,
    isLoading: subsLoading,
    error,
    refetch,
  } = useSubscriptions({
    page,
    limit: 20,
    search,
    plan: planFilter,
    active: activeOnly,
  });

  const { grantSubscription, cancelSubscription, isSaving } = useSubsMutations(() => {
    refetch();
    setShowGrantModal(false);
    setShowCancelModal(false);
    setSelectedSub(null);
    setGrantForm(DEFAULT_GRANT);
  });

  // ─── Handlers ────────────────────────────────────────────────────────────
  function handleSearch(v: string) {
    setSearch(v);
    setPage(1);
  }

  function handlePlanFilter(v: string) {
    setPlanFilter(v as 'basic' | 'premium' | 'family' | '');
    setPage(1);
  }

  function handleActiveOnly(v: boolean) {
    setActiveOnly(v);
    setPage(1);
  }

  function openCancelModal(sub: AdminSubscription) {
    setSelectedSub(sub);
    setShowCancelModal(true);
  }

  function openGrantModal() {
    setGrantForm(DEFAULT_GRANT);
    setFormError('');
    setShowGrantModal(true);
  }

  async function handleGrant() {
    if (!grantForm.userId.trim()) {
      setFormError('User ID là bắt buộc');
      return;
    }
    if (grantForm.days < 1 || grantForm.days > 365) {
      setFormError('Số ngày phải từ 1 đến 365');
      return;
    }
    setFormError('');
    await grantSubscription({
      user_id: grantForm.userId.trim(),
      plan: grantForm.plan,
      days: grantForm.days,
      note: grantForm.note || undefined,
    });
  }

  async function handleCancel() {
    if (!selectedSub) return;
    await cancelSubscription(selectedSub.id, selectedSub.user_email);
  }

  // ─── Table columns ───────────────────────────────────────────────────────
  const columns: Column<SubRow>[] = [
    {
      key: 'user',
      label: 'User',
      render: (row) => {
        const sub = row as unknown as AdminSubscription;
        return (
          <>
            <div className="cell-primary">{sub.user_email}</div>
            {sub.user_name && <div className="cell-muted">{sub.user_name}</div>}
          </>
        );
      },
    },
    {
      key: 'plan',
      label: 'Gói',
      render: (row) => <TierBadge tier={(row as unknown as AdminSubscription).plan} />,
    },
    {
      key: 'is_active',
      label: 'Trạng thái',
      render: (row) =>
        (row as unknown as AdminSubscription).is_active ? (
          <Badge variant="success">✅ Active</Badge>
        ) : (
          <Badge variant="danger">❌ Hết hạn</Badge>
        ),
    },
    {
      key: 'expires_at',
      label: 'Hết hạn',
      render: (row) => formatDate((row as unknown as AdminSubscription).expires_at),
    },
    {
      key: 'created_at',
      label: 'Tạo lúc',
      render: (row) => formatDateTime((row as unknown as AdminSubscription).created_at),
    },
    {
      key: 'payment_ref',
      label: 'Payment ref',
      render: (row) => {
        const ref = (row as unknown as AdminSubscription).payment_ref;
        return ref ? (
          <code style={{ fontSize: 11, color: 'var(--text-2)' }}>{ref}</code>
        ) : (
          '—'
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '80px',
      render: (row) => {
        const sub = row as unknown as AdminSubscription;
        return sub.is_active ? (
          <button
            className="btn btn-sm btn-danger"
            onClick={(e) => {
              e.stopPropagation();
              openCancelModal(sub);
            }}
          >
            🗑 Hủy
          </button>
        ) : null;
      },
    },
  ];

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div className="page-header__left">
          <h1>Gói đăng ký</h1>
          <p>Quản lý subscriptions thủ công</p>
        </div>
        <button className="btn btn-primary" onClick={openGrantModal}>
          + Cấp gói
        </button>
      </div>

      {/* Error */}
      {error && <ErrorBanner message={error} onRetry={refetch} />}

      {/* Table card */}
      <div className="card">
        {/* Toolbar */}
        <div className="table-toolbar">
          <div className="table-title">
            {total.toLocaleString('vi-VN')} subscriptions
          </div>
          <div className="filter-bar" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <SearchInput
              value={search}
              onChange={handleSearch}
              placeholder="Tìm email / tên..."
            />
            <select
              className="input"
              value={planFilter}
              onChange={(e) => handlePlanFilter(e.target.value)}
              style={{ width: 130 }}
            >
              <option value="">Tất cả gói</option>
              <option value="basic">Basic</option>
              <option value="premium">Premium</option>
              <option value="family">Family</option>
            </select>
            <Toggle
              checked={activeOnly}
              onChange={handleActiveOnly}
              label="Chỉ active"
            />
          </div>
        </div>

        {/* Table */}
        <div className="table-wrapper">
          <DataTable<SubRow>
            columns={columns}
            rows={subsData as SubRow[]}
            isLoading={subsLoading}
            emptyText="Không có subscription nào"
          />
        </div>

        {/* Pagination */}
        <Pagination
          page={page}
          totalPages={totalPages}
          totalRows={total}
          pageSize={20}
          onPageChange={setPage}
          hasPrev={page > 1}
          hasNext={page < totalPages}
        />
      </div>

      {/* ─── Grant Modal ──────────────────────────────────────────────────── */}
      <Modal
        isOpen={showGrantModal}
        onClose={() => setShowGrantModal(false)}
        title="Cấp gói đăng ký"
        actions={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setShowGrantModal(false)}
              disabled={isSaving}
            >
              Hủy
            </button>
            <button
              className="btn btn-primary"
              onClick={handleGrant}
              disabled={isSaving}
            >
              {isSaving ? 'Đang cấp...' : '✅ Cấp gói'}
            </button>
          </>
        }
      >
        {formError && (
          <div
            style={{
              marginBottom: 12,
              color: '#f87171',
              fontSize: 13,
              padding: '8px 12px',
              background: 'rgba(239,68,68,0.1)',
              borderRadius: 6,
            }}
          >
            ⚠️ {formError}
          </div>
        )}

        <div className="form-group">
          <label className="form-label">
            User ID <span style={{ color: '#f87171' }}>*</span>
          </label>
          <input
            className="input"
            type="text"
            placeholder="user_xxxx..."
            value={grantForm.userId}
            onChange={(e) => setGrantForm((f) => ({ ...f, userId: e.target.value }))}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Gói</label>
          <select
            className="input"
            value={grantForm.plan}
            onChange={(e) =>
              setGrantForm((f) => ({
                ...f,
                plan: e.target.value as 'basic' | 'premium' | 'family',
              }))
            }
          >
            <option value="basic">Basic</option>
            <option value="premium">Premium</option>
            <option value="family">Family</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">
            Số ngày <span style={{ color: 'var(--text-3)', fontSize: 12 }}>(1 – 365)</span>
          </label>
          <input
            className="input"
            type="number"
            min={1}
            max={365}
            value={grantForm.days}
            onChange={(e) =>
              setGrantForm((f) => ({ ...f, days: parseInt(e.target.value) || 30 }))
            }
          />
        </div>

        <div className="form-group">
          <label className="form-label">Ghi chú (tuỳ chọn)</label>
          <input
            className="input"
            type="text"
            placeholder="Ví dụ: Tặng dùng thử, chuyển khoản..."
            value={grantForm.note}
            onChange={(e) => setGrantForm((f) => ({ ...f, note: e.target.value }))}
          />
        </div>
      </Modal>

      {/* ─── Cancel Confirm Modal ─────────────────────────────────────────── */}
      <Modal
        isOpen={showCancelModal && selectedSub !== null}
        onClose={() => { setShowCancelModal(false); setSelectedSub(null); }}
        title="Hủy subscription"
        actions={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => { setShowCancelModal(false); setSelectedSub(null); }}
              disabled={isSaving}
            >
              Không
            </button>
            <button
              className="btn btn-danger"
              onClick={handleCancel}
              disabled={isSaving}
            >
              {isSaving ? 'Đang hủy...' : '🗑 Xác nhận hủy'}
            </button>
          </>
        }
      >
        {selectedSub && (
          <div style={{ lineHeight: 1.8 }}>
            <p style={{ marginBottom: 12, color: 'var(--text-2)' }}>
              Bạn có chắc muốn hủy subscription này không?
            </p>
            <div
              style={{
                background: 'var(--surface-2)',
                borderRadius: 8,
                padding: '12px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <div>
                <span style={{ color: 'var(--text-3)', fontSize: 12 }}>Email: </span>
                <strong style={{ color: 'var(--text-1)' }}>{selectedSub.user_email}</strong>
              </div>
              <div>
                <span style={{ color: 'var(--text-3)', fontSize: 12 }}>Gói: </span>
                <TierBadge tier={selectedSub.plan} />
              </div>
              <div>
                <span style={{ color: 'var(--text-3)', fontSize: 12 }}>Hết hạn: </span>
                <span style={{ color: 'var(--text-2)' }}>{formatDate(selectedSub.expires_at)}</span>
              </div>
            </div>
            <p style={{ marginTop: 12, color: '#f87171', fontSize: 13 }}>
              ⚠️ Hành động này không thể hoàn tác. User sẽ bị hạ xuống tier free.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
